/**
 * Application store (Zustand)
 * ───────────────────────────
 * Single source of truth for the running extension. Coordinates:
 *   • Tableau context + discovered parameter descriptors
 *   • The deferred draft of parameter values (edited freely, applied on Go)
 *   • Combinations, usage events, favorites, config
 *   • All mutating actions (executeGo, applyCombination, favorite CRUD)
 *
 * Persistence is delegated to the active StorageProvider; engines do the pure
 * computation. The store is the only place those two worlds meet.
 */

import { create } from "zustand";
import type {
  ActionType,
  Combination,
  ExtensionConfig,
  Favorite,
  ParameterDescriptor,
  ParameterValue,
  UsageEvent,
  ValidationRule,
} from "@/types";
import type { StorageProvider } from "@/lib/storage/types";
import { createStorage } from "@/lib/storage/storageFactory";
import { DEFAULT_CONFIG } from "@/lib/config";
import {
  initializeTableau,
  readTableauConfig,
  readWorkbookData,
  writeTableauConfig,
  writeWorkbookData,
  type TableauContext,
} from "@/lib/tableau/tableauClient";
import { discoverParameters } from "@/lib/tableau/parameterDiscovery";
import {
  applyParameters,
  buildAssignments,
} from "@/lib/tableau/parameterApply";
import { applyExecution, type ExecutionContext } from "@/lib/engines/combinationEngine";
import { buildUsageEvent, createFavorite, renameFavorite, togglePin } from "@/lib/engines/trackingEngine";
import { validate, type ValidationResult } from "@/lib/engines/validationEngine";
import { MOCK_DESCRIPTORS, buildMockHistory } from "@/lib/mockData";

export type Phase = "loading" | "ready" | "error";

interface AppState {
  phase: Phase;
  errorMessage: string | null;

  tableau: TableauContext | null;
  storage: StorageProvider | null;

  /** Every parameter discovered in the workbook (the full, unfiltered set). */
  allDescriptors: ParameterDescriptor[];
  /** The exposed subset the user actually controls (derived from config). */
  descriptors: ParameterDescriptor[];
  /** Deferred draft of values, keyed by parameterId. */
  draft: Record<string, ParameterValue>;
  /** True when the draft differs from the last applied values. */
  isDirty: boolean;
  /** True while a Go/apply is in flight. */
  isApplying: boolean;

  combinations: Combination[];
  events: UsageEvent[];
  favorites: Favorite[];
  config: ExtensionConfig;

  // ── derived ────────────────────────────────────────────────────────────────
  validation: ValidationResult;

  // ── lifecycle ────────────────────────────────────────────────────────────────
  initialize: () => Promise<void>;

  // ── draft editing ────────────────────────────────────────────────────────────
  setDraftValue: (parameterId: string, value: ParameterValue) => void;
  resetDraft: () => void;

  // ── execution ────────────────────────────────────────────────────────────────
  executeGo: () => Promise<void>;
  applyCombination: (combination: Combination, action: ActionType) => Promise<void>;

  // ── favorites ───────────────────────────────────────────────────────────────
  addFavorite: (combination: Combination, name: string) => Promise<void>;
  updateFavoriteName: (favoriteId: string, name: string) => Promise<void>;
  toggleFavoritePin: (favoriteId: string) => Promise<void>;
  removeFavorite: (favoriteId: string) => Promise<void>;

  // ── admin / config ───────────────────────────────────────────────────────────
  updateConfig: (patch: Partial<ExtensionConfig>) => Promise<void>;
  /** Include or exclude a single parameter from the control panel. */
  setParameterIncluded: (parameterId: string, included: boolean) => Promise<void>;
  /** Include every discovered parameter (reset to "expose all"). */
  includeAllParameters: () => Promise<void>;
  /** Re-discover parameters from the dashboard. With `keepData` (default) the
   * saved parameter selection and usage history survive; without it the setup
   * AND all tracked history are reset — the only action that clears them. */
  reloadConfiguration: (keepData?: boolean) => Promise<void>;
  setValidationRules: (rules: ValidationRule[]) => Promise<void>;
  resetAnalytics: () => Promise<void>;
}

/** Sentinel kept in `exposedParameterIds` when the user has excluded every
 * parameter, so the empty list doesn't collapse back to the "expose all"
 * default. Filtered out everywhere a real id is expected. */
const EXPOSE_NONE = "__none__";

/** Build the draft seed from descriptors' current values. */
function seedDraft(descriptors: ParameterDescriptor[]): Record<string, ParameterValue> {
  return Object.fromEntries(descriptors.map((d) => [d.id, d.currentValue]));
}

/** Apply admin "required" flags onto the full discovered parameter list. */
function applyRequired(
  all: ParameterDescriptor[],
  config: ExtensionConfig,
): ParameterDescriptor[] {
  return all.map((d) => ({
    ...d,
    required: config.requiredParameterIds.includes(d.id) || d.required,
  }));
}

/** Derive the exposed subset from the full list + admin exposure config.
 * Empty `exposedParameterIds` means "expose all" (the default). */
function exposedFrom(
  all: ParameterDescriptor[],
  config: ExtensionConfig,
): ParameterDescriptor[] {
  if (config.exposedParameterIds.length === 0) return all;
  const allow = new Set(config.exposedParameterIds);
  return all.filter((d) => allow.has(d.id));
}

/** Union two record lists by id; on conflict `pick` decides which wins. */
function mergeById<T extends { id: string }>(
  local: T[],
  workbook: T[],
  pick: (a: T, b: T) => T = (a) => a,
): T[] {
  const byId = new Map(local.map((x) => [x.id, x]));
  for (const wb of workbook) {
    const existing = byId.get(wb.id);
    byId.set(wb.id, existing ? pick(existing, wb) : wb);
  }
  return [...byId.values()];
}

/** On conflict keep the record with more recorded usage (ties → most recent). */
function pickRicherCombination(a: Combination, b: Combination): Combination {
  if (a.usageCount !== b.usageCount) return a.usageCount > b.usageCount ? a : b;
  return new Date(a.lastUsedAt) >= new Date(b.lastUsedAt) ? a : b;
}

/** Databricks connection details from config → createStorage options. */
function connectionOf(config: ExtensionConfig) {
  return {
    host: config.dbxHost,
    token: config.dbxToken,
    warehouse: config.dbxWarehouse,
    catalog: config.dbxCatalog,
    schema: config.dbxSchema,
  };
}

/** Snapshot the tracked history into the workbook so it survives reopening. */
function mirrorToWorkbook(state: Pick<AppState, "combinations" | "events" | "favorites">): void {
  writeWorkbookData({
    combinations: state.combinations,
    events: state.events,
    favorites: state.favorites,
  });
}

function revalidate(state: Pick<AppState, "descriptors" | "draft" | "config">): ValidationResult {
  return validate({
    descriptors: state.descriptors,
    values: state.draft,
    rules: state.config.validationRules,
  });
}

export const useAppStore = create<AppState>((set, get) => ({
  phase: "loading",
  errorMessage: null,
  tableau: null,
  storage: null,
  allDescriptors: [],
  descriptors: [],
  draft: {},
  isDirty: false,
  isApplying: false,
  combinations: [],
  events: [],
  favorites: [],
  config: DEFAULT_CONFIG,
  validation: { valid: true, errors: [], byParameter: {} },

  async initialize() {
    try {
      const tableau = await initializeTableau();

      // Load persisted config first so the right storage provider is chosen.
      const bootstrapStorage = await createStorage({
        kind: DEFAULT_CONFIG.storageProvider,
        restApiBaseUrl: DEFAULT_CONFIG.restApiBaseUrl,
        restApiKey: DEFAULT_CONFIG.restApiKey,
        table: DEFAULT_CONFIG.dbTable,
      });
      const persistedConfig = await bootstrapStorage.getConfig();
      // Config locked into the workbook (Tableau Settings) takes precedence — it
      // travels with the workbook and survives reloads on Server/Cloud.
      const lockedConfig = readTableauConfig();
      const config: ExtensionConfig = {
        ...DEFAULT_CONFIG,
        ...persistedConfig,
        ...lockedConfig,
      };

      // If admin configured a different provider, switch to it.
      let storage = bootstrapStorage;
      if (config.storageProvider !== bootstrapStorage.kind) {
        storage = await createStorage({
          kind: config.storageProvider,
          restApiBaseUrl: config.restApiBaseUrl,
          restApiKey: config.restApiKey,
          table: config.dbTable,
          connection: connectionOf(config),
        });
      }

      // Discover parameters (or mock when standalone).
      const discovered = tableau.available
        ? await discoverParameters()
        : MOCK_DESCRIPTORS;

      // Keep the full set for the admin selector; derive the exposed subset
      // (with required flags) that the user actually controls.
      const allDescriptors = applyRequired(discovered, config);
      const descriptors = exposedFrom(allDescriptors, config);

      // Load persisted data; seed mock history when standalone & empty. If the
      // primary provider fails mid-read (e.g. database went away after passing
      // its health check), degrade to browser storage — never the error screen.
      let combinations: Combination[] = [];
      let events: UsageEvent[] = [];
      let favorites: Favorite[] = [];
      try {
        [combinations, events, favorites] = await Promise.all([
          storage.getCombinations(),
          storage.getUsageEvents(),
          storage.getFavorites(),
        ]);
      } catch (err) {
        console.warn("[store] storage reads failed — falling back to browser storage", err);
        storage = await createStorage({ kind: "csv" });
        [combinations, events, favorites] = await Promise.all([
          storage.getCombinations(),
          storage.getUsageEvents(),
          storage.getFavorites(),
        ]);
      }

      if (!tableau.available && combinations.length === 0) {
        const mock = buildMockHistory();
        combinations = mock.combinations;
        events = mock.events;
        await Promise.all([
          ...combinations.map((c) => storage.putCombination(c)),
          ...events.map((e) => storage.appendUsageEvent(e)),
        ]);
      }

      // Merge in the history locked into the workbook. Browser storage doesn't
      // reliably survive Tableau Desktop restarts, so the workbook copy is what
      // makes Top Combinations show up on every open. Local (per-user) history
      // wins conflicts only when it has recorded more usage.
      const workbookData = tableau.available ? readWorkbookData() : null;
      if (workbookData) {
        combinations = mergeById(
          combinations,
          workbookData.combinations ?? [],
          pickRicherCombination,
        );
        events = mergeById(events, workbookData.events ?? []);
        favorites = mergeById(favorites, workbookData.favorites ?? []);
        // Sync the merged view back into browser storage for this session.
        await Promise.all([
          ...combinations.map((c) => storage.putCombination(c)),
          ...favorites.map((f) => storage.putFavorite(f)),
        ]).catch(() => undefined);
      }

      // Seed the draft from the full list so values survive include/exclude.
      const draft = seedDraft(allDescriptors);
      const validation = validate({ descriptors, values: draft, rules: config.validationRules });

      set({
        phase: "ready",
        tableau,
        storage,
        allDescriptors,
        descriptors,
        draft,
        isDirty: false,
        combinations,
        events,
        favorites,
        config,
        validation,
      });
    } catch (err) {
      console.error("[store] initialize failed", err);
      set({
        phase: "error",
        errorMessage: err instanceof Error ? err.message : String(err),
      });
    }
  },

  setDraftValue(parameterId, value) {
    const draft = { ...get().draft, [parameterId]: value };
    const validation = revalidate({ ...get(), draft });
    set({ draft, isDirty: true, validation });
  },

  resetDraft() {
    const draft = seedDraft(get().descriptors);
    set({ draft, isDirty: false, validation: revalidate({ ...get(), draft }) });
  },

  async executeGo() {
    const state = get();
    if (!state.validation.valid || state.isApplying) return;
    await runExecution(state.draft, "Manual_Go", set, get);
  },

  async applyCombination(combination, action) {
    // Populate the draft from the saved combination, then apply.
    const values: Record<string, ParameterValue> = { ...get().draft };
    for (const a of combination.assignments) values[a.parameterId] = a.value;
    set({ draft: values, validation: revalidate({ ...get(), draft: values }) });
    await runExecution(values, action, set, get);
  },

  async addFavorite(combination, name) {
    const { storage, favorites, tableau, config } = get();
    if (!storage) return;
    const creator = config.saveUserName ? tableau?.userName ?? "user" : "Anonymous";
    const fav = createFavorite(combination, name, creator, favorites.length);
    await storage.putFavorite(fav);
    set({ favorites: [...favorites, fav] });
    mirrorToWorkbook(get());
  },

  async updateFavoriteName(favoriteId, name) {
    const { storage, favorites } = get();
    if (!storage) return;
    const target = favorites.find((f) => f.id === favoriteId);
    if (!target) return;
    const updated = renameFavorite(target, name);
    await storage.putFavorite(updated);
    set({ favorites: favorites.map((f) => (f.id === favoriteId ? updated : f)) });
    mirrorToWorkbook(get());
  },

  async toggleFavoritePin(favoriteId) {
    const { storage, favorites } = get();
    if (!storage) return;
    const target = favorites.find((f) => f.id === favoriteId);
    if (!target) return;
    const updated = togglePin(target);
    await storage.putFavorite(updated);
    set({ favorites: favorites.map((f) => (f.id === favoriteId ? updated : f)) });
    mirrorToWorkbook(get());
  },

  async removeFavorite(favoriteId) {
    const { storage, favorites } = get();
    if (!storage) return;
    await storage.deleteFavorite(favoriteId);
    set({ favorites: favorites.filter((f) => f.id !== favoriteId) });
    mirrorToWorkbook(get());
  },

  async updateConfig(patch) {
    const { storage, config, allDescriptors, draft } = get();
    const next = { ...config, ...patch };

    // Recompute the exposed/required parameter view live when the admin changes
    // which parameters are included or marked required — no reload needed.
    const exposureChanged =
      patch.exposedParameterIds !== undefined ||
      patch.requiredParameterIds !== undefined;

    if (exposureChanged) {
      const nextAll = applyRequired(allDescriptors, next);
      const nextDescriptors = exposedFrom(nextAll, next);
      // Ensure newly-included parameters have a draft value seeded.
      const nextDraft = { ...draft };
      for (const d of nextAll) {
        if (!(d.id in nextDraft)) nextDraft[d.id] = d.currentValue;
      }
      set({
        config: next,
        allDescriptors: nextAll,
        descriptors: nextDescriptors,
        draft: nextDraft,
        validation: validate({
          descriptors: nextDescriptors,
          values: nextDraft,
          rules: next.validationRules,
        }),
      });
    } else {
      set({ config: next, validation: revalidate({ ...get(), config: next }) });
    }

    if (storage) await storage.putConfig(next);
    // Lock the configuration into the workbook so it persists across reloads.
    await writeTableauConfig(next);

    // Recreate the provider when it changed, or when its connection settings
    // (API URL / key / table) changed while the REST provider is active.
    const providerChanged = patch.storageProvider && patch.storageProvider !== storage?.kind;
    const restSettingsChanged =
      (next.storageProvider === "rest" || next.storageProvider === "cloud") &&
      (patch.restApiBaseUrl !== undefined ||
        patch.restApiKey !== undefined ||
        patch.dbTable !== undefined ||
        patch.dbxHost !== undefined ||
        patch.dbxToken !== undefined ||
        patch.dbxWarehouse !== undefined ||
        patch.dbxCatalog !== undefined ||
        patch.dbxSchema !== undefined);
    if (providerChanged || restSettingsChanged) {
      const newStorage = await createStorage({
        kind: next.storageProvider,
        restApiBaseUrl: next.restApiBaseUrl,
        restApiKey: next.restApiKey,
        table: next.dbTable,
        connection: connectionOf(next),
      });
      await newStorage.putConfig(next);
      set({ storage: newStorage });
    }
  },

  async setParameterIncluded(parameterId, included) {
    const { allDescriptors, config } = get();
    const validIds = new Set(allDescriptors.map((d) => d.id));
    // Materialise the current exposed set (empty = all), dropping stale ids.
    const current =
      config.exposedParameterIds.length === 0
        ? allDescriptors.map((d) => d.id)
        : config.exposedParameterIds.filter((x) => validIds.has(x));

    const draftNext = included
      ? [...new Set([...current, parameterId])]
      : current.filter((x) => x !== parameterId);

    await get().updateConfig({
      exposedParameterIds: draftNext.length === 0 ? [EXPOSE_NONE] : draftNext,
    });
  },

  async includeAllParameters() {
    await get().updateConfig({ exposedParameterIds: [] });
  },

  async reloadConfiguration(keepData = true) {
    const { tableau, storage, config } = get();
    // Re-read parameters straight from the dashboard (picks up any
    // added/removed in Tableau).
    const discovered = tableau?.available
      ? await discoverParameters()
      : MOCK_DESCRIPTORS;

    // keepData: preserve the include/exclude + required selection (dropping ids
    // that no longer exist). Otherwise reset the selection to defaults.
    const validIds = new Set(discovered.map((d) => d.id));
    const resetConfig: ExtensionConfig = keepData
      ? {
          ...config,
          exposedParameterIds: config.exposedParameterIds.filter(
            (id) => id === EXPOSE_NONE || validIds.has(id),
          ),
          requiredParameterIds: config.requiredParameterIds.filter((id) =>
            validIds.has(id),
          ),
        }
      : {
          ...config,
          exposedParameterIds: [],
          requiredParameterIds: [],
          configLocked: false,
        };
    const allDescriptors = applyRequired(discovered, resetConfig);
    const descriptors = exposedFrom(allDescriptors, resetConfig);
    const draft = seedDraft(allDescriptors);

    set({
      config: resetConfig,
      allDescriptors,
      descriptors,
      draft,
      isDirty: false,
      validation: validate({
        descriptors,
        values: draft,
        rules: resetConfig.validationRules,
      }),
    });

    if (storage) await storage.putConfig(resetConfig);
    await writeTableauConfig(resetConfig);

    // Starting from scratch also clears the tracked history everywhere —
    // browser storage, memory, and the copy locked into the workbook.
    if (!keepData) {
      await storage?.clearAll?.();
      set({ combinations: [], events: [], favorites: [] });
      mirrorToWorkbook(get());
    }
  },

  async setValidationRules(rules) {
    await get().updateConfig({ validationRules: rules });
  },

  async resetAnalytics() {
    const { storage } = get();
    await storage?.clearAll?.();
    set({ combinations: [], events: [], favorites: [] });
    // Also clear the workbook copy, or the old history would re-merge on the
    // next open.
    mirrorToWorkbook(get());
  },
}));

/**
 * Shared execution path for Manual_Go and saved-combination clicks. Applies the
 * values to Tableau, records the combination + audit event, and persists.
 */
async function runExecution(
  values: Record<string, ParameterValue>,
  actionType: ActionType,
  set: (partial: Partial<AppState>) => void,
  get: () => AppState,
): Promise<void> {
  const { descriptors, storage, tableau, combinations, events, config } = get();
  if (!storage) return;

  set({ isApplying: true });
  try {
    // 1. Push to Tableau (no-op in dev mock mode).
    if (tableau?.available) {
      const result = await applyParameters(descriptors, values);
      if (result.failures.length) {
        console.warn("[store] some parameters failed to apply", result.failures);
      }
    }

    // 2. Build assignments capturing intent (even if a write failed).
    const assignments = buildAssignments(descriptors, values);

    // 3. Track combination + event (respecting the tracking toggle).
    if (config.trackingEnabled) {
      const ctx: ExecutionContext = {
        // Admin privacy toggle: record who ran the combination, or anonymise.
        userName: config.saveUserName ? tableau?.userName ?? "user" : "Anonymous",
        workbookName: tableau?.workbookName ?? "Workbook",
        dashboardName: tableau?.dashboardName ?? "Dashboard",
        actionType,
      };
      const { combination } = applyExecution(combinations, assignments, ctx);
      const event = buildUsageEvent(combination, assignments, ctx);

      await storage.putCombination(combination);
      await storage.appendUsageEvent(event);

      const nextCombinations = combinations.some((c) => c.id === combination.id)
        ? combinations.map((c) => (c.id === combination.id ? combination : c))
        : [...combinations, combination];

      set({ combinations: nextCombinations, events: [...events, event] });
      // Lock the updated history into the workbook (authoring mode only).
      mirrorToWorkbook(get());
    }

    set({ isDirty: false });
  } catch (err) {
    console.error("[store] execution failed", err);
    set({ errorMessage: err instanceof Error ? err.message : String(err) });
  } finally {
    set({ isApplying: false });
  }
}
